import React from "react";
import clsx from "clsx";
import { type ResultsProps } from "@usereactify/search";

import { IconChevronLeft, IconChevronRight } from "@bared/ui";

type NumberedPaginationProps = React.ComponentProps<
  NonNullable<ResultsProps["renderPaginationNumbered"]>
>;

type PaginationNumberItemProps = {
  page: number;
  currentPage: number;
  buildPagePath: NumberedPaginationProps["buildPagePath"];
  handlePageChange: NumberedPaginationProps["handlePageChange"];
  dataTestId: string;
};

type PaginationNavButtonProps = {
  type: "next" | "prev";
  toPage: number;
  buildPagePath: NumberedPaginationProps["buildPagePath"];
  onClickHandler:
    | NumberedPaginationProps["handleNextPage"]
    | NumberedPaginationProps["handlePreviousPage"];
};

type PaginationBreakItemProps = {
  dataTestId: string;
};

const paginationNavSettings = {
  prev: {
    label: "Goto previous page",
    icon: <IconChevronLeft className="h-mini w-mini" />,
  },
  next: {
    label: "Goto next page",
    icon: <IconChevronRight className="h-mini w-mini" />,
  },
};

const PaginationNavButton = ({
  type,
  toPage,
  buildPagePath,
  onClickHandler,
}: PaginationNavButtonProps) => {
  return (
    <li>
      <a
        className="[> aria-disabled]:text-grey [> aria-disabled]:cursor-default flex h-maxi w-maxi items-center justify-center"
        aria-label={paginationNavSettings[type].label}
        rel={type}
        href={buildPagePath(toPage)}
        onClick={(event) => {
          onClickHandler(event);

          window.requestAnimationFrame(() => {
            window.scrollTo({
              top: 0,
              behavior: "smooth",
            });
          });
        }}
        data-testid={type}
      >
        {paginationNavSettings[type].icon}
      </a>
    </li>
  );
};

const PaginationBreakItem = ({ dataTestId }: PaginationBreakItemProps) => (
  <li>
    <span
      className="mx-pico flex h-maxi w-maxi items-center justify-center"
      data-testid={dataTestId}
    >
      ...
    </span>
  </li>
);

const PaginationNumberItem = ({
  page,
  currentPage,
  buildPagePath,
  handlePageChange,
  dataTestId,
}: PaginationNumberItemProps) => {
  const isCurrentPage = page === currentPage;
  const ariaLabel = isCurrentPage
    ? `Current Page ${page + 1}`
    : `Goto Page  ${page + 1}`;
  const href = isCurrentPage ? undefined : buildPagePath(page + 1);
  const onClickHandler = (event) => {
    handlePageChange(page, event);

    window.requestAnimationFrame(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    });
  };

  return (
    <li>
      <a
        className={clsx(
          "border-grey mx-pico flex h-maxi w-maxi items-center justify-center border text-body text-primary",
          isCurrentPage
            ? "border-none bg-primary text-white"
            : "bg-transparent hover:border-none hover:bg-tertiary",
        )}
        href={href}
        aria-label={ariaLabel}
        aria-current={isCurrentPage}
        onClick={isCurrentPage ? undefined : onClickHandler}
        data-testid={dataTestId}
      >
        {page + 1}
      </a>
    </li>
  );
};

const PaginationNav = ({
  currentPage,
  buildPagePath,
  handlePreviousPage,
  handleNextPage,
  pagesToShow,
  totalPages,
  handlePageChange,
}: NumberedPaginationProps) => {
  const show = {
    prev: currentPage >= 1,
    first: !pagesToShow.includes(0),
    leadingBreak: !pagesToShow.includes(1) && pagesToShow?.length > 1,
    laggingBreak:
      !pagesToShow.includes(totalPages - 2) && pagesToShow?.length > 1,
    last: !pagesToShow.includes(totalPages - 1),
    next: currentPage + 1 < totalPages,
  };

  return (
    <nav aria-label="Pagination">
      <ul className="mb-[40px] mt-midi flex px-mini">
        {show.prev && (
          <PaginationNavButton
            key="prev"
            type="prev"
            toPage={currentPage}
            buildPagePath={buildPagePath}
            onClickHandler={handlePreviousPage}
          />
        )}
        {show.first && (
          <PaginationNumberItem
            key="first"
            page={0}
            currentPage={currentPage}
            buildPagePath={buildPagePath}
            handlePageChange={handlePageChange}
            dataTestId="firstpagequicklink"
          />
        )}
        {show.leadingBreak && (
          <PaginationBreakItem key="leadbreak" dataTestId="leadbreak" />
        )}
        {pagesToShow.map((page) => (
          <PaginationNumberItem
            key={page}
            page={page}
            currentPage={currentPage}
            buildPagePath={buildPagePath}
            handlePageChange={handlePageChange}
            dataTestId={`page ${page + 1}`}
          />
        ))}
        {show.laggingBreak && (
          <PaginationBreakItem key="lagbreak" dataTestId="lagbreak" />
        )}
        {show.last && (
          <PaginationNumberItem
            key="last"
            page={totalPages - 1}
            currentPage={currentPage}
            buildPagePath={buildPagePath}
            handlePageChange={handlePageChange}
            dataTestId="lastpagequicklink"
          />
        )}
        {show.next && (
          <PaginationNavButton
            key="next"
            type="next"
            toPage={currentPage + 2}
            buildPagePath={buildPagePath}
            onClickHandler={handleNextPage}
          />
        )}
      </ul>
    </nav>
  );
};

const NumberedPagination = (props: NumberedPaginationProps) => {
  if (props.totalPages <= 1) {
    return;
  }
  return (
    <div className="flex w-full justify-end">
      <PaginationNav {...props}></PaginationNav>
    </div>
  );
};

export default NumberedPagination;
